/**
 * Documentation Home Page
 *
 * Welcome page with quick links to major sections.
 */

import { Link } from 'react-router-dom';

const sections = [
  {
    title: 'Getting Started',
    description: 'Learn the basics and get up and running quickly.',
    href: '/docs/getting-started/quick-start',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    title: 'Guides for Org Owners',
    description: 'Set up your organization and manage members.',
    href: '/docs/guides/org-owners/setup-organization',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    title: 'Guides for Admins',
    description: 'Manage events, crew, assignments, and more.',
    href: '/docs/guides/admins/events',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  },
  {
    title: 'API Reference',
    description: 'Complete API documentation with examples.',
    href: '/docs/api',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
];

const popularGuides = [
  { title: 'Creating Events', href: '/docs/guides/admins/events' },
  { title: 'Managing Crew', href: '/docs/guides/admins/crew' },
  { title: 'Assigning Crew to Events', href: '/docs/guides/admins/assignments' },
  { title: 'Sending Messages', href: '/docs/guides/admins/messaging' },
  { title: 'Running Reports', href: '/docs/guides/admins/reports' },
];

export default function DocsHome() {
  return (
    <div className="not-prose">
      {/* Hero */}
      <div className="pb-8 border-b border-gray-200">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          ProgramRow Documentation
        </h1>
        <p className="mt-4 text-lg text-gray-600 max-w-3xl">
          Learn how to use ProgramRow to manage your events, crew, and assignments.
          Whether you're an organization owner, admin, or developer integrating
          with our API, we've got you covered.
        </p>
      </div>

      {/* Section cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
        {sections.map((section) => (
          <Link
            key={section.href}
            to={section.href}
            className="group relative bg-white border border-gray-200 rounded-lg p-6 hover:border-primary-300 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-rack focus:ring-offset-2"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center group-hover:bg-primary-600 group-hover:text-white transition-colors">
                {section.icon}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                  {section.title}
                  <span className="absolute inset-0" aria-hidden="true" />
                </h2>
                <p className="mt-1 text-sm text-gray-600">{section.description}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Popular guides */}
      <div className="mt-12">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Popular Guides</h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {popularGuides.map((guide) => (
            <li key={guide.href}>
              <Link
                to={guide.href}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 hover:underline"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                {guide.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Quick start callout */}
      <div className="mt-12 bg-gray-50 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900">New to ProgramRow?</h2>
        <p className="mt-2 text-gray-600">
          Start with our{' '}
          <Link to="/docs/getting-started/quick-start" className="text-primary-600 font-medium hover:underline">
            Quick Start Guide
          </Link>{' '}
          to learn the basics, or explore{' '}
          <Link to="/docs/getting-started/concepts" className="text-primary-600 font-medium hover:underline">
            Core Concepts
          </Link>{' '}
          to understand how ProgramRow works.
        </p>
      </div>
    </div>
  );
}
